/**
 * Der Social-Plan im Kunden-Admin: die Beitraege des Monats und der eigenen
 * Aktionen pruefen, anpassen, freigeben.
 *
 * Bis die Plattformen verbunden sind, postet der Kunde von Hand: Text kopieren,
 * Medien oeffnen, danach „Gepostet“ mit Link melden. Ohne Freigabe wird nichts
 * gepostet — auch spaeter nicht automatisch.
 *
 * Bei medizinnahen Sites wird ein Text, den der Kunde geaendert hat, vor der
 * Freigabe gegen das Heilmittelwerbegesetz geprueft; die Befunde stehen an der
 * Karte (ab 0.15.0).
 */

import { useState } from "react";

import { HubError } from "../core/client.js";
import type {
  HubAktionNeu, HubSocialFreigabe, HubSocialPlan, HubSocialPost, HubSocialPostAendern, HubTikTokAngaben,
} from "../core/types.js";
import { useSocialPlaene, useTikTokCreator } from "./hooks.js";

export const KANAL: Record<string, string> = {
  instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", tiktok: "TikTok",
};
const ART: Record<string, string> = { post: "Beitrag", carousel: "Carousel", reel: "Reel", video: "Video" };
const STATUS: Record<string, string> = {
  proposed: "vorgeschlagen",
  approved: "freigegeben",
  rejected: "verworfen",
  posted: "gepostet",
  publishing: "wird gepostet …",
  publish_failed: "nicht gepostet",
};

export interface PlanListeProps {
  basePath?: string;
  userName?: string;
}

function monat(periode: string): string {
  const [jahr, m] = periode.split("-").map(Number);
  if (!jahr || !m) return periode;
  return new Date(jahr, m - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

function tag(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function termin(iso: string | null): string {
  if (!iso) return "ohne Termin";
  return new Date(iso).toLocaleString("de-DE", {
    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

/** ISO → Wert fuer <input type="datetime-local"> in Ortszeit des Browsers. */
export function lokal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const z = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
}

function wocheVon(iso: string | null): string {
  if (!iso) return "Ohne Termin";
  const d = new Date(iso);
  const montag = new Date(d);
  montag.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `Woche ab ${montag.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}`;
}

function datumIn(tage: number): string {
  const d = new Date();
  d.setDate(d.getDate() + tage);
  const z = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

/** Fehlermeldung plus — bei einer abgelehnten Pruefung — die Befunde. */
export function fehlerText(e: unknown): { text: string; befunde: string[] } {
  if (e instanceof HubError) return { text: e.message, befunde: e.befunde };
  return { text: e instanceof Error ? e.message : String(e), befunde: [] };
}

export function PlanListe({ basePath, userName = "" }: PlanListeProps) {
  const s = useSocialPlaene(basePath);
  const [neueAktion, setNeueAktion] = useState(false);

  if (s.loading && !s.data) return <p className="ch-muted">Lädt …</p>;
  if (s.error) return <p className="ch-hinweis">Der Social-Plan konnte nicht geladen werden: {s.error}</p>;
  const plaene = s.data ?? [];
  return (
    <div className="ch-planung">
      <div className="ch-aktionen">
        <button type="button" className="ch-knopf" onClick={() => setNeueAktion((v) => !v)}>
          {neueAktion ? "Schließen" : "Aktion planen"}
        </button>
        <span className="ch-muted ch-klein">Eigener Zeitraum und Fokus — etwa ein Fest, ein Angebot, eine Woche.</span>
      </div>
      {neueAktion && (
        <AktionFormular userName={userName} anlegen={async (a) => { await s.aktionAnlegen(a); setNeueAktion(false); }} />
      )}
      {plaene.length === 0 && (
        <p className="ch-muted">
          Noch kein Social-Plan. Er entsteht monatlich am 20. für den Folgemonat aus den Fotos und Videos,
          die Sie in der Mediathek zur Verwendung freigegeben haben.
        </p>
      )}
      {plaene.map((plan) => (
        <PlanBlock key={plan.id} plan={plan} userName={userName} aendern={s.aendern} freigeben={s.freigeben}
          gepostet={s.gepostet} loeschen={s.loeschen} erneut={s.erneut} aktionLoeschen={s.aktionLoeschen}
          neuLaden={s.reload} />
      ))}
    </div>
  );
}

function AktionFormular({ userName, anlegen }: { userName: string; anlegen: (a: HubAktionNeu) => Promise<void> }) {
  const [titel, setTitel] = useState("");
  const [fokus, setFokus] = useState("");
  const [von, setVon] = useState(datumIn(7));
  const [bis, setBis] = useState(datumIn(20));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFehler(null);
    try {
      await anlegen({ title: titel, focus: fokus, starts_on: von, ends_on: bis, by: userName });
    } catch (err) {
      setFehler(fehlerText(err).text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="ch-karte ch-formular" onSubmit={absenden} style={{ marginBottom: 12 }}>
      <label className="ch-klein ch-muted" htmlFor="ch-aktion-titel">Name der Aktion</label>
      <input id="ch-aktion-titel" className="ch-eingabe" value={titel} onChange={(e) => setTitel(e.target.value)}
        placeholder="z. B. Sommerfest" required />
      <label className="ch-klein ch-muted" htmlFor="ch-aktion-fokus">Worum soll es gehen?</label>
      <textarea id="ch-aktion-fokus" className="ch-feld" rows={3} value={fokus} onChange={(e) => setFokus(e.target.value)}
        placeholder="z. B. Einladung zum Sommerfest am 12. Juli, Kinderprogramm, Anmeldung nicht nötig" />
      <div className="ch-auswahl">
        <label>Von <input className="ch-eingabe" type="date" value={von} min={datumIn(0)} onChange={(e) => setVon(e.target.value)} required /></label>
        <label>Bis <input className="ch-eingabe" type="date" value={bis} min={von} onChange={(e) => setBis(e.target.value)} required /></label>
      </div>
      <p className="ch-muted ch-klein">
        Wir planen Beiträge aus Ihrer Mediathek für diesen Zeitraum — sie kommen zur Freigabe wie der Monatsplan.
      </p>
      <div className="ch-aktionen">
        <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy || !titel.trim()}>Aktion planen lassen</button>
      </div>
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </form>
  );
}

interface BlockProps {
  plan: HubSocialPlan;
  userName: string;
  aendern: (id: number, patch: HubSocialPostAendern) => Promise<void>;
  freigeben: (planId: number, by?: string) => Promise<HubSocialFreigabe>;
  gepostet: (id: number, url?: string, by?: string) => Promise<void>;
  loeschen: (id: number) => Promise<void>;
  erneut: (id: number, trotzdem?: boolean) => Promise<void>;
  aktionLoeschen: (planId: number) => Promise<void>;
  neuLaden: () => void;
}

function PlanBlock({ plan, userName, aendern, freigeben, gepostet, loeschen, erneut, aktionLoeschen, neuLaden }: BlockProps) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const aktion = plan.kind === "campaign";
  const offen = plan.posts.filter((p) => p.status === "proposed").length;
  const wochen: { label: string; posts: HubSocialPost[] }[] = [];
  for (const p of plan.posts) {
    const label = wocheVon(p.scheduled_at);
    const letzte = wochen[wochen.length - 1];
    if (letzte && letzte.label === label) letzte.posts.push(p);
    else wochen.push({ label, posts: [p] });
  }

  async function tun(arbeit: () => Promise<unknown>) {
    setBusy(true);
    setFehler(null);
    try {
      await arbeit();
    } catch (e) {
      setFehler(fehlerText(e).text);
    } finally {
      setBusy(false);
    }
  }

  async function alleFreigeben() {
    await tun(async () => {
      const ergebnis = await freigeben(plan.id, userName);
      if (ergebnis.blockiert) {
        setFehler(`${ergebnis.blockiert} Beitrag/Beiträge nicht freigegeben — der Text muss angepasst werden (siehe Karte).`);
      }
    });
  }

  return (
    <section className="ch-plan">
      <div className="ch-kopf">
        <h3 className="ch-titel">
          {aktion ? `Aktion: ${plan.title} · ${tag(plan.starts_on)}–${tag(plan.ends_on)}` : `Social-Plan ${monat(plan.period_month)}`}
        </h3>
        <span className="ch-muted ch-klein">
          {plan.status === "draft" ? "wird geplant …" : `${plan.posts.length} Beiträge · ${offen} offen`}
          {plan.status === "approved" && plan.approved_by ? ` · freigegeben von ${plan.approved_by}` : ""}
        </span>
      </div>
      {aktion && plan.focus && <p className="ch-muted ch-klein">{plan.focus}</p>}
      {plan.error && <p className="ch-hinweis">{plan.error}</p>}
      {plan.note && <p className="ch-hinweis" style={{ whiteSpace: "pre-line" }}>{plan.note}</p>}
      <div className="ch-aktionen">
        {offen > 0 && (
          <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy} onClick={() => void alleFreigeben()}>
            Alle {offen} offenen Beiträge freigeben
          </button>
        )}
        {plan.status === "draft" && (
          <button type="button" className="ch-knopf" onClick={neuLaden}>Neu laden</button>
        )}
        {aktion && !plan.posts.some((p) => p.status === "approved" || p.status === "posted") && (
          <button type="button" className="ch-knopf" disabled={busy}
            onClick={() => void tun(() => aktionLoeschen(plan.id))}>Aktion zurückziehen</button>
        )}
      </div>
      {fehler && <p className="ch-hinweis">{fehler}</p>}
      {wochen.map((w) => (
        <div key={w.label}>
          <p className="ch-woche">{w.label}</p>
          <ul className="ch-karten">
            {w.posts.map((p) => (
              <PostKarte key={`${p.id}-${p.status}`} post={p} userName={userName} aendern={aendern} gepostet={gepostet}
                loeschen={loeschen} erneut={erneut} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export interface PostKarteProps {
  post: HubSocialPost;
  userName: string;
  aendern: (id: number, patch: HubSocialPostAendern) => Promise<void>;
  gepostet: (id: number, url?: string, by?: string) => Promise<void>;
  /** Nur fuer eigene Beitraege — Vorschlaege werden verworfen, nicht geloescht. */
  loeschen?: (id: number) => Promise<void>;
  /** Gescheitertes automatisches Posten erneut versuchen (ab 0.16.0). */
  erneut?: (id: number, trotzdem?: boolean) => Promise<void>;
}

export function PostKarte({ post: p, userName, aendern, gepostet, loeschen, erneut }: PostKarteProps) {
  const [caption, setCaption] = useState(p.caption);
  const [tags, setTags] = useState(p.hashtags.join(" "));
  const [wann, setWann] = useState(lokal(p.scheduled_at));
  const [linkOffen, setLinkOffen] = useState(false);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [fehler, setFehler] = useState<{ text: string; befunde: string[] } | null>(null);
  const geaendert = caption !== p.caption || tags !== p.hashtags.join(" ") || wann !== lokal(p.scheduled_at);
  const fertig = p.status === "posted" || p.status === "publishing";
  const gescheitert = p.status === "publish_failed";
  const eigen = p.origin === "customer";
  const befunde = fehler?.befunde.length ? fehler.befunde : p.check && !p.check.ok ? p.check.befunde : [];

  async function tun(aktion: () => Promise<void>, erfolg?: string) {
    setBusy(true);
    setFehler(null);
    setInfo(null);
    try {
      await aktion();
      if (erfolg) setInfo(erfolg);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  function patch(): HubSocialPostAendern {
    const out: HubSocialPostAendern = { caption, hashtags: tags.split(/\s+/).filter(Boolean), by: userName };
    if (wann && wann !== lokal(p.scheduled_at)) out.scheduled_at = new Date(wann).toISOString();
    return out;
  }

  async function kopieren() {
    const text = [caption.trim(), tags.trim()].filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setInfo("Text kopiert.");
    } catch {
      setFehler({ text: "Kopieren ging nicht — Text bitte von Hand markieren.", befunde: [] });
    }
  }

  return (
    <li className="ch-karte">
      <div className="ch-kopf">
        <span>
          <strong>{KANAL[p.channel] ?? p.channel}</strong> · {ART[p.content_type] ?? p.content_type} · {termin(p.scheduled_at)}
          {eigen && <span className="ch-muted"> · eigener Beitrag</span>}
        </span>
        <span className={p.status === "approved" || fertig ? "ch-plakette ch-plakette-ok" : "ch-plakette"}>
          {STATUS[p.status] ?? p.status}
        </span>
      </div>

      {p.media.length > 0 && (
        <div className="ch-post-medien">
          {p.media.map((m) => (
            <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer" title={m.description || m.filename}>
              {m.preview_url
                ? <img src={m.preview_url} alt={m.description || m.filename} loading="lazy" />
                : <span className="ch-klein ch-muted">{m.kind === "video" ? "Video" : "Bild"}</span>}
            </a>
          ))}
        </div>
      )}

      {fertig ? (
        <>
          <p className="ch-text" style={{ whiteSpace: "pre-line" }}>{p.caption}</p>
          {p.posted_url && (
            <p className="ch-muted ch-klein">
              Gepostet: <a href={p.posted_url} target="_blank" rel="noopener noreferrer">zum Beitrag</a>
              {p.decided_by ? ` (${p.decided_by})` : ""}
            </p>
          )}
        </>
      ) : (
        <>
          <label className="ch-klein ch-muted" htmlFor={`caption-${p.id}`}>Text</label>
          <textarea id={`caption-${p.id}`} className="ch-feld" rows={5} value={caption}
            onChange={(e) => setCaption(e.target.value)} />
          <label className="ch-klein ch-muted" htmlFor={`tags-${p.id}`}>Hashtags</label>
          <input id={`tags-${p.id}`} className="ch-eingabe" value={tags} onChange={(e) => setTags(e.target.value)} />
          <label className="ch-klein ch-muted" htmlFor={`wann-${p.id}`}>Termin</label>
          <input id={`wann-${p.id}`} className="ch-eingabe" type="datetime-local" value={wann}
            onChange={(e) => setWann(e.target.value)} />
        </>
      )}

      {p.publish_error && !fertig && (
        <p className={gescheitert ? "ch-hinweis" : "ch-muted ch-klein"}>{p.publish_error}</p>
      )}
      {gescheitert && erneut && (
        <div className="ch-aktionen">
          <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
            onClick={() => void tun(async () => {
              try {
                await erneut(p.id);
              } catch (e) {
                // Facebook nach unklarem Ausgang: erst nachsehen lassen.
                if (e instanceof HubError && e.code === "unklar"
                  && window.confirm(`${e.message}\n\nIst der Beitrag NICHT auf der Seite? Dann erneut versuchen.`)) {
                  await erneut(p.id, true);
                  return;
                }
                throw e;
              }
            }, "Wird erneut versucht.")}>
            Erneut versuchen
          </button>
        </div>
      )}

      {p.channel === "tiktok" && !fertig && (
        <TikTokAngabenFormular post={p} userName={userName} aendern={aendern} />
      )}

      {befunde.length > 0 && !fertig && (
        <div className="ch-hinweis">
          <strong>{fehler?.text && !fehler.text.includes("Heilmittel") && p.channel === "tiktok"
            ? "So noch nicht freigebbar:" : "So noch nicht freigebbar (Heilmittelwerbegesetz):"}</strong>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{befunde.map((b) => <li key={b}>{b}</li>)}</ul>
        </div>
      )}

      {linkOffen ? (
        <form className="ch-aktionen" onSubmit={(e) => {
          e.preventDefault();
          void tun(() => gepostet(p.id, link.trim(), userName), "Als gepostet vermerkt.");
          setLinkOffen(false);
        }}>
          <input className="ch-eingabe" type="url" placeholder="Link zum Beitrag (optional)" value={link}
            onChange={(e) => setLink(e.target.value)} pattern="https://.*" />
          <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy}>Speichern</button>
          <button type="button" className="ch-knopf" onClick={() => setLinkOffen(false)} disabled={busy}>Abbrechen</button>
        </form>
      ) : !fertig && (
        <div className="ch-aktionen">
          {geaendert && (
            <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
              onClick={() => void tun(() => aendern(p.id, patch()), "Gespeichert.")}>
              Änderungen speichern
            </button>
          )}
          {p.status !== "approved" && (
            <button type="button" className={geaendert ? "ch-knopf" : "ch-knopf ch-knopf-primaer"} disabled={busy}
              onClick={() => void tun(() => aendern(p.id, { ...(geaendert ? patch() : {}), status: "approved", by: userName }))}>
              {geaendert ? "Speichern und freigeben" : "Freigeben"}
            </button>
          )}
          {p.status !== "rejected" && !eigen && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(() => aendern(p.id, { status: "rejected", by: userName }))}>Verwerfen</button>
          )}
          {p.status === "rejected" && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(() => aendern(p.id, { status: "proposed", by: userName }))}>Wieder vorschlagen</button>
          )}
          {eigen && loeschen && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(() => loeschen(p.id))}>Löschen</button>
          )}
          {p.status === "approved" && (
            <>
              <button type="button" className="ch-knopf" onClick={() => void kopieren()}>Text kopieren</button>
              <button type="button" className="ch-knopf" onClick={() => setLinkOffen(true)}>Gepostet</button>
            </>
          )}
        </div>
      )}
      {info && <p className="ch-muted ch-klein">{info}</p>}
      {fehler && !fehler.befunde.length && <p className="ch-hinweis">{fehler.text}</p>}
    </li>
  );
}

const SICHTBARKEIT: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Alle",
  MUTUAL_FOLLOW_FRIENDS: "Freunde",
  FOLLOWER_OF_CREATOR: "Follower",
  SELF_ONLY: "Nur ich",
};

const LEER: HubTikTokAngaben = {
  privacy_level: "", allow_comment: null, allow_duet: null, allow_stitch: null,
  werbung: false, eigene_marke: false, markenpartner: false, musik_zustimmung: false,
};

/**
 * Die Angaben, die TikTok fuer jeden Beitrag vom Nutzer verlangt (ab 0.18.0):
 * Sichtbarkeit aus den Moeglichkeiten des Kontos (ohne Voreinstellung),
 * Kommentare/Duett/Stitch, Werbekennzeichnung und die Zustimmung zur
 * Musiknutzung. Ohne sie gibt die Engine den Beitrag nicht frei.
 */
function TikTokAngabenFormular({ post: p, userName, aendern }: {
  post: HubSocialPost;
  userName: string;
  aendern: (id: number, patch: HubSocialPostAendern) => Promise<void>;
}) {
  const [offen, setOffen] = useState(!p.tiktok?.musik_zustimmung);
  const creator = useTikTokCreator(offen);
  const [a, setA] = useState<HubTikTokAngaben>({ ...LEER, ...(p.tiktok ?? {}) });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const c = creator.data;
  const setze = (patch: Partial<HubTikTokAngaben>) => setA((v) => ({ ...v, ...patch }));

  if (!offen) {
    return (
      <p className="ch-muted ch-klein">
        TikTok: {SICHTBARKEIT[a.privacy_level] ?? a.privacy_level}
        {a.werbung ? " · als Werbung gekennzeichnet" : ""}{" "}
        <button type="button" className="ch-knopf" onClick={() => setOffen(true)}>Angaben ändern</button>
      </p>
    );
  }

  async function speichern() {
    setBusy(true);
    setFehler(null);
    try {
      // Nicht angehakt heisst „nicht erlaubt“ — nichts ist vorausgewaehlt, wie TikTok es verlangt.
      await aendern(p.id, {
        tiktok: { ...a, allow_comment: a.allow_comment === true, allow_duet: a.allow_duet === true,
          allow_stitch: a.allow_stitch === true },
        by: userName,
      });
      setOffen(false);
    } catch (e) {
      setFehler(fehlerText(e).text);
    } finally {
      setBusy(false);
    }
  }

  const schalter = (feld: "allow_comment" | "allow_duet" | "allow_stitch", name: string, gesperrt: boolean) => (
    <label key={feld}>
      <input type="checkbox" checked={a[feld] === true} disabled={gesperrt}
        onChange={(e) => setze({ [feld]: e.target.checked } as Partial<HubTikTokAngaben>)} />
      {name}{gesperrt ? " (im Konto abgeschaltet)" : ""}
    </label>
  );

  return (
    <div className="ch-karte ch-formular" style={{ margin: "8px 0" }}>
      {creator.loading && !c ? (
        <p className="ch-muted ch-klein">TikTok-Konto wird abgefragt …</p>
      ) : creator.error ? (
        <p className="ch-hinweis">TikTok ist nicht verbunden oder gerade nicht erreichbar: {creator.error}</p>
      ) : c && (
        <>
          <p className="ch-klein" style={{ margin: "0 0 6px" }}>
            Posten als <strong>{c.nickname || c.username}</strong>
            {c.username ? <span className="ch-muted"> @{c.username}</span> : null}
          </p>
          <label className="ch-klein ch-muted" htmlFor={`tt-sicht-${p.id}`}>Wer darf das Video sehen?</label>
          <select id={`tt-sicht-${p.id}`} className="ch-eingabe" value={a.privacy_level}
            onChange={(e) => setze({ privacy_level: e.target.value })}>
            <option value="">— bitte wählen —</option>
            {c.privacy_level_options.map((o) => <option key={o} value={o}>{SICHTBARKEIT[o] ?? o}</option>)}
          </select>
          <div className="ch-auswahl">
            {schalter("allow_comment", "Kommentare erlauben", c.comment_disabled)}
            {schalter("allow_duet", "Duett erlauben", c.duet_disabled)}
            {schalter("allow_stitch", "Stitch erlauben", c.stitch_disabled)}
          </div>
          <label className="ch-klein" style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={a.werbung}
              onChange={(e) => setze({ werbung: e.target.checked, ...(e.target.checked ? {} : { eigene_marke: false, markenpartner: false }) })} />
            Dieses Video bewirbt eine Marke, ein Produkt oder eine Leistung
          </label>
          {a.werbung && (
            <div className="ch-auswahl" style={{ marginLeft: 22 }}>
              <label><input type="checkbox" checked={a.eigene_marke} onChange={(e) => setze({ eigene_marke: e.target.checked })} />
                Eigene Marke („Werbeinhalt“)</label>
              <label><input type="checkbox" checked={a.markenpartner} disabled={a.privacy_level === "SELF_ONLY"}
                onChange={(e) => setze({ markenpartner: e.target.checked })} />
                Markenpartner („Bezahlte Partnerschaft“){a.privacy_level === "SELF_ONLY" ? " — nicht bei „Nur ich“" : ""}</label>
            </div>
          )}
          <label className="ch-klein" style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 6 }}>
            <input type="checkbox" checked={a.musik_zustimmung} onChange={(e) => setze({ musik_zustimmung: e.target.checked })} />
            <span>
              Mit dem Posten stimme ich der{" "}
              <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/de" target="_blank"
                rel="noopener noreferrer">Musiknutzungsbestätigung</a>
              {a.markenpartner ? <> und der <a href="https://www.tiktok.com/legal/page/global/bc-policy/de"
                target="_blank" rel="noopener noreferrer">Richtlinie für Markeninhalte</a></> : null} von TikTok zu.
            </span>
          </label>
          <div className="ch-aktionen">
            <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy} onClick={() => void speichern()}>
              TikTok-Angaben speichern
            </button>
          </div>
          {fehler && <p className="ch-hinweis">{fehler}</p>}
        </>
      )}
    </div>
  );
}
